import { supabase } from "~/integrations/supabase/client";

interface BSaleClientData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  company?: string;
  address?: string;
  city?: string;
  municipality?: string;
  activity?: string;
  code?: string; // RUT/tax ID
}

export const syncClientToBSale = async (
  bsaleClientId: string | number,
  clientData: BSaleClientData
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      "update-bsale-client",
      {
        body: {
          bsaleClientId: bsaleClientId.toString(),
          clientData,
        },
      }
    );

    if (error) {
      console.error("Error calling update-bsale-client function:", error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      console.error("BSale API error:", data);
      return {
        success: false,
        error: data?.error || "Unknown error updating BSale client",
      };
    }

    console.log("BSale client updated successfully:", data);
    return { success: true };
  } catch (error) {
    console.error("Error syncing client to BSale:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

export const findBSaleClientByRUT = async (
  rut: string
): Promise<{ bsaleClientId?: string; error?: string }> => {
  try {
    // Since we can't pass query params directly with supabase.functions.invoke,
    // we'll need to make a direct fetch call
    const supabaseUrl = supabase.supabaseUrl;
    const supabaseKey = supabase.supabaseKey;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/find-bsale-client?code=${encodeURIComponent(rut)}&limit=1`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${supabaseKey}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { error: errorData.error || "Failed to search BSale client" };
    }

    const data = await response.json();

    // Check if client was found
    if (data?.items && data.items.length > 0) {
      return { bsaleClientId: data.items[0].id };
    }

    return { error: "Client not found in BSale" };
  } catch (error) {
    console.error("Error finding BSale client:", error);
    return {
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
